
import { FormControl, FormErrorMessage, Radio, RadioGroup, Stack, Switch, useBreakpointValue } from '@chakra-ui/react'
import { YesNoFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsPropertyDto, InsurerDiscount } from 'BrokerTsLib/src/dtos'
import { Product, Risk, RiskVariant, Selection } from 'BrokerTsLib/src/enums'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { CustomNumberInput, CustomSlider, FormLabel } from '@/components'
import { SliderLabel } from '@/components/ui/SliderLabel'
import CompaniesSelection from '@/containers/forms/CompaniesSelection'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useInsurers, useRiskData } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useGetInsuranceParamsDiscounts } from '@/hooks/useInsuranceParams'

export const ParametersForm = ({ onDiscountsLoaded }: { onDiscountsLoaded: (discounts: InsurerDiscount[]) => void }) => {
  const { product } = useApp()
  const isProperty = product === Product.PropertyInsurance

  const methods = useFormContext<InsuranceParamsPropertyDto>()

  const {
    setValue,
    watch,
    formState: { errors },
    register,
    getValues
  } = methods

  const selectedInsurers = watch('baseParams.insurers') ?? []
  const spolVariantId = watch('property.data.spolVariantId')
  const liabilityVariantId = watch('property.liability')
  const responsibilityRealestateVariantId = watch('property.responsibilityRealestate')
  const floodInsuranceRequired = watch('property.data.floodInsuranceRequired')

  const { insuranceParams: ip, insuranceParamsId, isDisabled } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsPropertyDto

  const { data: allInsurersData } = useInsurers(false, false)
  const allInsurers = allInsurersData?.data ?? []

  useEffect(() => {
    // reset ve formulari, pokud se zmeni pocatek z headeru
    setValue('generalParams.insuranceStartDate', insuranceParams?.generalParams.insuranceStartDate)
  }, [insuranceParams?.generalParams.insuranceStartDate])

  useEffect(() => {
    if ((allInsurers.length ?? 0) > 0 && getValues('baseParams.insurers')?.length == 0)
      setValue('baseParams.insurers', allInsurers.map(({ id }) => id))
  }, [allInsurers.length])

  useEffect(() => {
    setValue('property.insurers', selectedInsurers)
  }, [selectedInsurers])

  const [insurersDiscounts, setInsurersDiscounts] = useState<InsurerDiscount[] | undefined>(undefined)
  useGetInsuranceParamsDiscounts(insuranceParamsId!, {}, 
    (data) => {
      if (data) {
        setInsurersDiscounts(data)
        setValue('baseParams.insurerDiscounts', data ?? [])
      }
      onDiscountsLoaded(data ?? [])
    }
  )


  const {
    selected: selectedSpol,
    value: spol,
    sliderDS: spolSliderDS,
    min: spolMin,
    max: spolMax,
  } = useRiskData(Risk.SPOL_PROPERTY, [{ riskVariantId: spolVariantId, riskId: Risk.SPOL_PROPERTY }], [])

  const {
    selected: selectedLiability,
    value: liability,
    sliderDS: liabilitySliderDS,
    min: liabilityMin,
    max: liabilityMax,
  } = useRiskData(Risk.RESPONSIBILITY, [{ riskVariantId: liabilityVariantId, riskId: Risk.RESPONSIBILITY }], [])

  const {
    selected: selectedResponsibilityRealestate,
    value: responsibilityRealestate,
    sliderDS: responsibilityRealestateSliderDS,
    min: responsibilityRealestateMin,
    max: responsibilityRealestateMax,
  } = useRiskData(Risk.RESPONSIBILITY_REALESTATE, [{ riskVariantId: responsibilityRealestateVariantId, riskId: Risk.RESPONSIBILITY_REALESTATE }], [])

  const insurerSelectionColumnsCount = useBreakpointValue(
    {
      base: 1,
      md: 2,
    }
  )

  const typeError = errors.property?.type as FieldError

  const hasHouseholdAndRealtyVariant = !!insuranceParams.property.variants
    ?.find(x => x.variantParts
      ?.find(x => x.risk == Risk.PROPERTY_TYPE)
      ?.riskVariant == RiskVariant.HouseholdAndRealty
    )

  const realtyTypeId = getValues('property.data.realtyTypeId')

  return (
    <>
      {isProperty &&
        <FormRow>
          {hasHouseholdAndRealtyVariant &&
            <FormControl isInvalid={!!errors.property?.type}>
              <FormLabel>Typ pojištění</FormLabel>

              <RadioGroup
                onChange={(x) => setValue('property.type', parseInt(x))}
                value={watch('property.type')?.toString()}
                isDisabled={isDisabled}
              >
                <Stack>
                  <Radio value={RiskVariant.Household.toString()}>
                    Domácnost
                  </Radio>
                  <Radio value={RiskVariant.Realty.toString()}>
                    Nemovitost
                  </Radio>
                  <Radio value={RiskVariant.HouseholdAndRealty.toString()}>
                    Domácnost + nemovitost
                  </Radio>
                </Stack>
              </RadioGroup>

              {typeError && (
                <FormErrorMessage>
                  {typeError.message}
                </FormErrorMessage>
              )}
            </FormControl>
          }

          <FormControl isInvalid={!!errors.property?.range}>
            <FormLabel>Rozsah pojištění</FormLabel>

            <RadioGroup
              onChange={(x) => setValue('property.range', parseInt(x))}
              value={watch('property.range')?.toString()}
              isDisabled={isDisabled}
            >
              <Stack>
                <Radio value={RiskVariant.MiniRange.toString()}>
                  Nižší
                </Radio>
                <Radio value={RiskVariant.MaxiRange.toString()}>
                  Vyšší
                </Radio>
              </Stack>
            </RadioGroup>

            {errors.property?.range && (
              <FormErrorMessage>
                {errors.property?.range.message}
              </FormErrorMessage>
            )}
          </FormControl>
        </FormRow>
      }

      {isProperty &&
        <FormRow>
          {spolSliderDS && spolMin && spolMax && (
            <FormControl sx={{ gap: 4 }}>

              <SliderLabel
                label="Spoluúčast"
                value={spol?.name ?? ''}
              />

              <CustomSlider
                value={
                  spolSliderDS.find(
                    (x) => x.id == selectedSpol?.riskVariantId,
                  )?.i ?? 0
                }
                labels={[
                  { label: spolMin?.name ?? '', value: spolMin?.i },
                  { label: spolMax?.name ?? '', value: spolMax?.i },
                ]}
                min={0}
                max={spolSliderDS.length - 1}
                step={1}
                onChange={(val) => {
                  setValue('property.data.spolVariantId', spolSliderDS.find(x => x.i == val)?.id)
                }}
                isDisabled={isDisabled}
              />
            </FormControl>
          )}
        </FormRow>
      }

      <FormRow itemsCount={1}>
        {liabilitySliderDS && liabilityMin && liabilityMax && (
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label="Odpovědnost z běžného života"
              tooltip='Pojištění odpovědnosti z činností v běžném životě - kryje újmy/škody na zdraví, na věci a finanční škody. Zvolte minimální výši limitu, který požadujete. V případě, že pojišťovna nabízí jiný limit, tak bude nastaveno na nejbližší vyšší limit.'
              value={liability?.name ?? ''}
            />

            <CustomSlider
              value={
                liabilitySliderDS.find(
                  (x) => x.id == selectedLiability?.riskVariantId,
                )?.i ?? 0
              }
              labels={[
                { label: liabilityMin?.name ?? '', value: liabilityMin?.i },
                { label: liabilityMax?.name ?? '', value: liabilityMax?.i },
              ]}
              min={0}
              max={liabilitySliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setValue('property.liability', liabilitySliderDS.find(x => x.i == val)?.id)
              }}
              isDisabled={isDisabled}
            />
          </FormControl>
        )}

        {responsibilityRealestateSliderDS && responsibilityRealestateMin && responsibilityRealestateMax && (
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              tooltip='Pojištění odpovědnosti vlastníka nemovitosti za škody z držby nemovitosti - kryje újmy/škody na zdraví, na věci a finanční škody. Zvolte minimální výši limitu, který požadujete. V případě, že pojišťovna nabízí jiný limit, tak bude nastaveno na nejbližší vyšší limit.'
              label="Odpovědnost z vlastnictví nemovitosti"
              value={responsibilityRealestate?.name ?? ''}
            />

            <CustomSlider
              value={
                responsibilityRealestateSliderDS.find(
                  (x) => x.id == selectedResponsibilityRealestate?.riskVariantId,
                )?.i ?? 0
              }
              labels={[
                { label: responsibilityRealestateMin?.name ?? '', value: responsibilityRealestateMin?.i },
                { label: responsibilityRealestateMax?.name ?? '', value: responsibilityRealestateMax?.i },
              ]}
              min={0}
              max={responsibilityRealestateSliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setValue('property.responsibilityRealestate', responsibilityRealestateSliderDS.find(x => x.i == val)?.id)
              }}
              isDisabled={isDisabled}
            />
          </FormControl>
        )}
      </FormRow>


      {isProperty &&
        <FormRow itemsCount={2} sx={{ rowGap: 4 }}>
          <YesNoFormControl name='property.data.floodInsuranceRequired'
            label="Povodeň, záplava"
            labelProps={{ hint: 'Povodní se rozumí zaplavení územních celků vodou, která se vylila z břehů vodních toků nebo nádrží nebo která břehy a hráze protrhla a nebo bylo zaplavení způsobeno náhlým a náhodným zmenšením průtočného profilu vodního toku. Záplavou se rozumí vytvoření souvislé vodní plochy, která po určitou dobu stojí nebo proudí v místě, např. vystoupnutím odpadních vod z kanalizačního nebo odpadního potrubí následkem povodně a záplavy.' }}
            isDisabled={isDisabled}
          />

          {floodInsuranceRequired &&
            <>
              <FormRow gridColumn='1 / -1' backgroundColor='gray.100' borderRadius='sm' p={6} rowGap={8}>
                <FormControl>
                  <FormLabel
                    tooltip='Kolikrát se v místě pojištění v posledních 10 letech vyskytla povodeň nebo záplava? Pokud se záplava nebo povodeň vyskytla více než jednou, nelze pojištění u pojišťovny Axa sjednat.'
                  >
                    Počet povodní v uplynulých 10 letech
                  </FormLabel>

                  <CustomNumberInput
                    min={0}
                    max={100}
                    w='100%'
                    defaultValue={getValues('property.data.floodOccurence10')}
                    onValueChange={(_, value) => setValue('property.data.floodOccurence10', value)}
                    isDisabled={isDisabled}
                    inputRightElement={null}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    tooltip='Kolikrát se v místě pojištění v posledních 20 letech vyskytla povodeň nebo záplava? Pokud se záplava nebo povodeň vyskytla více než jednou, dojde u Kooperativy k vyloučení škod vzniklých působením pojistných nebezpečí povodeň nebo záplava.'
                  >
                    Počet povodní v uplynulých 20 letech
                  </FormLabel>

                  <CustomNumberInput
                    min={0}
                    max={100}
                    w='100%'
                    defaultValue={getValues('property.data.floodOccurence')}
                    onValueChange={(_, value) => setValue('property.data.floodOccurence', value)}
                    isDisabled={isDisabled}
                    inputRightElement={null}
                  />
                </FormControl>

                {realtyTypeId == Selection.Byt && (
                  <FormControl>
                    <FormLabel tooltip="Nachází se byt pojištěného v bezpečném podlaží bytového domu (tzn. minimálně 3m nad úrovní okolního terénu)? Pouze pro pojišťovnu Kooperativa.">
                      Bezpečné podlaží vůči povodni
                    </FormLabel>

                    <Switch
                      {...register('property.data.floodSafeAppartment')}
                      isChecked={watch('property.data.floodSafeAppartment') ?? false}
                      isDisabled={isDisabled}
                    />
                  </FormControl>
                )}
              </FormRow>
            </>
          }
        </FormRow>
      }

      <FormRow itemsCount={1}>
        <CompaniesSelection
          allInsurers={allInsurers}
          allInsurersDiscounts={insurersDiscounts ?? []}
          insurersPath={'baseParams.insurers'}
          insurersDiscountsPath='baseParams.insurerDiscounts'
          columnsCount={insurerSelectionColumnsCount}
        />
      </FormRow>
    </>
  )
}
